import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { useCreateVenueClosure } from '@/hooks/useCreateVenueClosure'
import { ApiError, toApiError, type ConflictDetail } from '@/lib/api/errors'
import type { VenueClosureCreateInput } from '@/lib/venue/closureTypes'
import { closureConfirmMessageGroup } from '@/lib/venue/closureViewModel'

/** 요구사항 원문 확인 모달 문구 — 휴무형(HOLIDAY) vs 차단형(BLOCK·RECURRING_BLOCK·LESSON), 설계 §2 자율 결정 A2. */
const CONFIRM_NOTICE: Record<'HOLIDAY' | 'BLOCK_LIKE', string> = {
  HOLIDAY:
    '선택한 날짜를 전체 코트 휴무일로 등록합니다. 해당 날짜의 모든 예약 가능 시간이 즉시 예약 불가로 전환됩니다',
  BLOCK_LIKE: '선택한 코트와 시간대를 예약 불가로 등록합니다. 변경 사항은 사용자 예약 화면에 즉시 반영됩니다',
}

function conflictText(conflict: ConflictDetail): string {
  return `겹치는 예약이 ${conflict.count}건 있습니다(확정 ${conflict.confirmedCount}건 · 진행중 ${conflict.pendingCount}건). 그대로 등록해도 이 예약들은 취소되지 않습니다. 그래도 등록할까요?`
}

/**
 * 등록 확인 모달(요구사항 원문 문구, 설계 §2). "확정 예약이 있으면 건수를 알리고 진행 여부를
 * 확인받는다" — 1차 확인(`force:false`)이 409(예약 충돌)로 거절되면 모달 안에서 건수 문구로
 * 전환하고, 재확인 시에만 `force:true` 로 재요청한다(`CourtDeleteConfirmModal.tsx` 의
 * "모달 안에서 에러로 전환" 패턴 + 재시도 버튼).
 */
export function ClosureCreateConfirmModal({
  venueId,
  input,
  onClose,
}: {
  venueId: string
  input: Omit<VenueClosureCreateInput, 'force'>
  onClose: () => void
}) {
  const [conflict, setConflict] = useState<ConflictDetail | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const createMutation = useCreateVenueClosure(venueId)

  async function handleConfirm(force: boolean) {
    setError(null)
    try {
      await createMutation.mutateAsync({ ...input, force })
      onClose()
    } catch (thrown) {
      const apiError = toApiError(thrown)
      if (apiError.conflict) {
        setConflict(apiError.conflict)
        return
      }
      setError(apiError)
    }
  }

  const messageGroup = closureConfirmMessageGroup(input.type)

  return (
    <Modal title="일정 등록" onClose={onClose}>
      <div className="space-y-4">
        {error ? (
          <Alert tone="danger">
            <p>{error.message}</p>
          </Alert>
        ) : conflict ? (
          <Alert tone="warning">{conflictText(conflict)}</Alert>
        ) : (
          <Alert tone="info">{CONFIRM_NOTICE[messageGroup]}</Alert>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            disabled={createMutation.isPending}
            onClick={() => {
              void handleConfirm(conflict !== null)
            }}
          >
            {createMutation.isPending ? '등록하는 중…' : conflict ? '그래도 등록' : '등록'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
